import { useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ArrowLeftRight, Camera } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Decoration } from "../product-types";

const AURORA_STYLE: CSSProperties = { background: "var(--gradient-aurora)" };

export function DecorationLayer({ decoration }: { decoration: Decoration }) {
  const needsAurora = decoration.kind === "aurora" || decoration.kind === "auroraBlob";
  return (
    <div
      aria-hidden
      className={decoration.className}
      style={needsAurora ? AURORA_STYLE : undefined}
    />
  );
}

/** Section wrapper: shared border/background + decorative layers. */
export function SectionShell({
  bgClassName,
  relative = false,
  border = true,
  decorations = [],
  children,
}: {
  bgClassName?: string;
  relative?: boolean;
  border?: boolean;
  decorations?: Decoration[];
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        relative && "relative overflow-hidden",
        border && "border-b border-border/60",
        bgClassName,
      )}
    >
      {decorations.map((decoration) => (
        <DecorationLayer key={decoration.className} decoration={decoration} />
      ))}
      {children}
    </section>
  );
}

export function ImagePlaceholder({ label, className }: { label: string; className?: string }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/50 bg-muted/20",
        className ?? "aspect-[3/2]",
      )}
    >
      <Camera className="h-8 w-8 text-muted-foreground/30" />
      <p className="text-xs text-muted-foreground/40">{label}</p>
    </div>
  );
}

/** Before/after image comparison slider (drag the handle). */
export function RevealSlider({
  opaque,
  transparent,
  width,
  height,
  revealLabel,
  imageAlt,
}: {
  opaque: string;
  transparent: string;
  width: number;
  height: number;
  revealLabel: string;
  imageAlt: string;
}) {
  const [revealPercent, setRevealPercent] = useState(50);
  const frameRef = useRef<HTMLDivElement>(null);

  const setRevealFromX = (clientX: number) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = ((clientX - rect.left) / rect.width) * 100;
    setRevealPercent(Math.min(100, Math.max(0, Math.round(next))));
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
      <div
        ref={frameRef}
        role="slider"
        tabIndex={0}
        aria-label={revealLabel}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={revealPercent}
        className="relative aspect-[3/2] cursor-ew-resize touch-none overflow-hidden outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        onPointerDown={(event) => {
          event.preventDefault();
          event.currentTarget.setPointerCapture(event.pointerId);
          setRevealFromX(event.clientX);
        }}
        onPointerMove={(event) => {
          if (event.buttons > 0) {
            setRevealFromX(event.clientX);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            setRevealPercent((current) => Math.max(0, current - 5));
          }
          if (event.key === "ArrowRight") {
            event.preventDefault();
            setRevealPercent((current) => Math.min(100, current + 5));
          }
          if (event.key === "Home") {
            event.preventDefault();
            setRevealPercent(0);
          }
          if (event.key === "End") {
            event.preventDefault();
            setRevealPercent(100);
          }
        }}
      >
        <img
          src={opaque}
          alt={imageAlt}
          width={width}
          height={height}
          className="absolute inset-0 h-full w-full object-cover"
          draggable={false}
          loading="lazy"
        />
        <div
          aria-hidden
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - revealPercent}% 0 0)` }}
        >
          <img
            src={transparent}
            alt=""
            width={width}
            height={height}
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
            loading="lazy"
          />
        </div>
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 z-10 w-px bg-primary shadow-[0_0_18px_rgba(22,136,243,0.8)]"
          style={{ left: `${revealPercent}%` }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-1/2 z-10 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-4 border-background/70 bg-foreground/75 text-background shadow-glow backdrop-blur"
          style={{ left: `${revealPercent}%` }}
        >
          <ArrowLeftRight className="h-5 w-5 stroke-[2.5]" />
        </div>
      </div>
    </div>
  );
}
